define([
  'axios',
  'lib/sumome/service/index',
  'underscore',
  'moment',
  'articles',
  'attributes',
  'es6!launcher/util/listbuilder/embedded/util',
  'es6!156085c5-0017-4150-b225-a731ad248f38/service/displayrules',
  'es6!156085c5-0017-4150-b225-a731ad248f38/service/listbuilder/listbuilder',
  'es6!156085c5-0017-4150-b225-a731ad248f38/service/scrollbox/scrollbox',
  'es6!156085c5-0017-4150-b225-a731ad248f38/service/smartbar/smartbar',
  'es6!156085c5-0017-4150-b225-a731ad248f38/service/welcomemat/welcomemat',
  'es6!156085c5-0017-4150-b225-a731ad248f38/service/game/game',
  'es6!156085c5-0017-4150-b225-a731ad248f38/service/clicktriggers',
  'es6!launcher/util/listbuilder/constants',
  'es6!launcher/util/discounts/ecommerce',
], (
  axios, SumoService, _, moment, Articles, Attributes, EmbeddedUtil, DisplayRules,
  ListBuilderService, ScrollBoxService, SmartBarService, WelcomeMatService,
  GameService, ClickTriggers, Constants, Ecommerce,
) => {
  const { getCart } = Ecommerce;

  const SUBSCRIBED_COOKIE = '__smSubscribed';
  const CTA_COOKIE = '__smClicked';
  const PUSH_SUBSCRIBED_COOKIE = '__smPushSubscribed';

  const {
    FormTypes,
    FormGoals,
  } = Constants;

  const getServiceForFormType = (formType) => {
    switch(formType) {
      case FormTypes.SCROLL_BOX:
        return ScrollBoxService;
      case FormTypes.SMART_BAR:
        return SmartBarService;
      case FormTypes.WELCOME_MAT:
        return WelcomeMatService;
      case FormTypes.GAME:
        return GameService;
      default:
        return ListBuilderService;
    }
  };

  const byFormType = type => variant => variant.form_type === type;

  class AppService extends SumoService.Service {
    constructor(api) {
      super(api);

      this.services = [];
      this.forms = [];

      this.shouldShow = this.shouldShow.bind(this);
      this.showVariants = this.showVariants.bind(this);
      this.showVariant = this.showVariant.bind(this);
      this.onCloseWelcomeMat = this.onCloseWelcomeMat.bind(this);
      this.performVariantPreload = this.performVariantPreload.bind(this);
      this.fetchProofEvents = this.fetchProofEvents.bind(this);

      const cookieKeysOnly = Object.keys(this.getCookie());

      this.loadingCart = false;
      this.cartLoaded = false;

      this.options = {
        listBuilder3Service: true,
        location: window.location.toString(),
        width: Math.min(window.screen.availWidth, window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth),
        articles: Articles.findAll().length,
        cookie_names: cookieKeysOnly,
        referrer: document.referrer,
        geoLocation: api.properties.geoLocation,
        pageViews: api.properties.views,
        userAgent: navigator.userAgent,
        embedCodeIds: EmbeddedUtil.getManuallyEmbeddedIds(),
      };

      window.setTimeout(this.load.bind(this), 0);
    }

    loadCart() {
      this.loadingCart = true;

      getCart().then((cart) => {
        this.cartLoaded = true;

        if (!cart) {
          this.cartTotal = 0;
          this.cartItems = [];
        } else {
          this.cartTotal = cart.total_price ? cart.total_price / 100 : 0;
          this.cartItems = cart.items || [];
        }
      });
    }

    load() {
      const {
        variants,
        success,
        previewForm,
        currency,
      } = this.api.properties.serviceData.listBuilder3Service;

      if (previewForm) {
        // Only trigger the preview form. Nothing else matters. Life is suffering.
        previewForm.json = JSON.parse(previewForm.json);
        return this.showVariant(previewForm, { isPreview: true });
      }

      // Set up click triggers separately
      this.triggers = new ClickTriggers(new ListBuilderService(this.api, this));
      this.triggers.bindClickTriggers(this.options);

      if (success && variants && variants.length) {
        const filteredVariants = variants.filter(variant => (
           variant.form_type !== FormTypes.INLINE_POPUP
        ));
        const inlines = variants.filter(variant => (
          variant.form_type === FormTypes.INLINE_POPUP
        ));

        this.handleVariants(filteredVariants);
        this.handleInlines(inlines);
      }
    }

    handleVariants(variants) {
      const {
        views: pageviews = 1,
        geoLocation,
      } = this.api.properties;

      // Filter out variants that shouldn't show
      const possibleVariants = variants.filter(variant => this.shouldShow(variant));

      let hasCartDisplayRules = false;

      possibleVariants.forEach((variant) => {
        variant.display_rules.forEach((rule) => {
          if (rule.filter_type.indexOf('cart') === 0) {
            hasCartDisplayRules = true;
          }
        });
      });

      if (hasCartDisplayRules && !this.cartLoaded) {
        // Fetch cart total before filtering rules
        if (!this.loadingCart) {
          this.loadCart();
        }

        setTimeout(() => {
          this.handleVariants(variants);
        }, 100);

        return;
      }

      // Filter out popups based on display rules
      const byDisplayRules = opts => (variant) => {
        const options = {
          ...opts,
          cartTotal: this.cartTotal,
          cartItems: this.cartItems,
        };

        return (
          !variant.requires_client_check
          || DisplayRules.isCurrentUrlAllowed(
            variant.display_rules,
            geoLocation,
            pageviews,
            options,
          )
        );
      };

      if (possibleVariants.length > 0) {
        // Select random forms from remaining list
        this.forms = [];
        const possiblePopups = possibleVariants
          .filter(byFormType(FormTypes.POPUP))
          .filter(byDisplayRules());

        // Random popup
        let randomPopup;
        if (possiblePopups && possiblePopups.length) {
          randomPopup = possiblePopups[Math.floor(Math.random() * possiblePopups.length)];
          this.forms.push(randomPopup);
        }

        const possibleGames = possibleVariants
          .filter(byFormType(FormTypes.GAME))
          .filter(byDisplayRules({ popupForm: randomPopup }));

        // Random Game
        let randomGame;
        if (possibleGames && possibleGames.length) {
          randomGame = possibleGames[Math.floor(Math.random() * possibleGames.length)];
          this.forms.push(randomGame);
        }

        // Random Smart Bar
        const possibleSmartBars = possibleVariants
          .filter(byFormType(FormTypes.SMART_BAR))
          .filter(byDisplayRules({ popupForm: randomPopup || randomGame }));

        if (possibleSmartBars && possibleSmartBars.length) {
          const randomSmartBar = possibleSmartBars[Math.floor(Math.random() * possibleSmartBars.length)];
          this.forms.push(randomSmartBar);
        }

        // Random Scroll Box
        const possibleScrollBoxes = possibleVariants
          .filter(byFormType(FormTypes.SCROLL_BOX))
          .filter(byDisplayRules({ popupForm: randomPopup || randomGame }));

        if (possibleScrollBoxes && possibleScrollBoxes.length) {
          const randomScrollBox = possibleScrollBoxes[Math.floor(Math.random() * possibleScrollBoxes.length)];
          this.forms.push(randomScrollBox);
        }

        // Random Welcome Mat
        const possibleWelcomeMats = possibleVariants
          .filter(byFormType(FormTypes.WELCOME_MAT))
          .filter(byDisplayRules({ popupForm: randomPopup || randomGame }));

        let randomWelcomeMat;
        if (possibleWelcomeMats && possibleWelcomeMats.length) {
          randomWelcomeMat = possibleWelcomeMats[Math.floor(Math.random() * possibleWelcomeMats.length)];

          // Show embedded/parallax mats alongside other form types
          if (randomWelcomeMat && randomWelcomeMat.display_mode !== 'normal') {
            this.forms.push(randomWelcomeMat);
          }
        }

        if (randomWelcomeMat && randomWelcomeMat.display_mode === 'normal') {
          this.showVariant(randomWelcomeMat);
        } else {
          this.showVariants();
        }
      }
    }

    handleInlines(variants) {
      const ctry = (this.api.properties.geoLocation && this.api.properties.geoLocation.ctry) ? this.api.properties.geoLocation.ctry : false;
      const pageviews = this.api.properties.views || 1;
      let possibleVariants = variants.filter(variant => this.shouldShow(variant) && (!variant.requires_client_check || DisplayRules.isCurrentUrlAllowed(variant.display_rules, ctry, pageviews)));

      for (let i = 0; i < possibleVariants.length; i++) {
        const variant = possibleVariants[i];

        if ((variant.legacy_id && !variant.json) || variant.embed_mode === 'auto') {
          this.showVariant(variant);
        } else if (variant.embed_mode === 'manual') {
          // Find Manual Anchors by id
          const manualAnchors = EmbeddedUtil.findManualAnchors(variant.campaign_id, variant.legacyEmbedCodes || []);

          // Embed one variant at each manual anchor
          for (let i = 0; i < manualAnchors.length; i++) {
            this.showVariant(_.extend({
              anchorEl: manualAnchors[i],
            }, variant));
          }
        }
      }
    }

    shouldShow(variant) {
      const Service = getServiceForFormType(variant.form_type);
      let shown = false;

      let shownCookie = Service.SHOWN_COOKIE;

      if (variant.form_type === FormTypes.WELCOME_MAT && variant.display_mode !== 'normal') {
        shownCookie = Service.EMBEDDED_SHOWN_COOKIE;
      }

      if (variant.form_type === FormTypes.INLINE_POPUP) {
        if (!variant.ignore_cookie) {
          switch (variant.goal) {
            case 'emails':
              shown = this.getSubscribedCookie(SUBSCRIBED_COOKIE);
              break;

            case 'cta':
              shown = this.getCtaCookie(CTA_COOKIE);
              break;

            case 'push':
              shown = this.getPushSubscribedCookie(PUSH_SUBSCRIBED_COOKIE);
              break;

            default:
              break;
          }
        }

        if (variant.optout_cookie && this.getOptOutCookie(Service.OPT_OUT_COOKIE)) {
          shown = true;
        }
      } else {
        if (variant.ignore_cookie) {
          shown = this.getShownCookie(shownCookie, variant);
        } else {
          if (variant.cookie_lifetime_interval === 'always') {
            switch (variant.goal) {
              case 'emails':
                shown = this.getSubscribedCookie(SUBSCRIBED_COOKIE);
                break;

              case 'cta':
                shown = this.getCtaCookie(CTA_COOKIE);
                break;

              case 'push':
                shown = this.getPushSubscribedCookie(PUSH_SUBSCRIBED_COOKIE);
                break;

              default:
                break;
            }
          } else {
            switch (variant.goal) {
              case 'emails':
                shown = (this.getShownCookie(shownCookie, variant) || this.getSubscribedCookie(SUBSCRIBED_COOKIE));
                break;

              case 'cta':
                shown = (this.getShownCookie(shownCookie, variant) || this.getCtaCookie(CTA_COOKIE));
                break;

              case 'push':
                shown = (
                  this.getShownCookie(shownCookie, variant)
                  || this.getPushSubscribedCookie(PUSH_SUBSCRIBED_COOKIE)
                );
                break;

              default:
                shown = this.getShownCookie(shownCookie, variant);
                break;
            }
          }
        }

        if (variant.optout_cookie && this.getOptOutCookie(Service.OPT_OUT_COOKIE)) {
          shown = true;
        }

        // prevent showing push subscribe request on non-compatible browsers
        if (variant.goal === 'push' && this.isNotPushCompatible()) {
          shown = true;
        }
      }

      // Click Triggers and Embedded Popups are handled differently
      // TODO: When inline is switched to a form_type, that can be checked here.
      return !shown;
    }

    showVariants() {
      for (let i = 0; i < this.forms.length; i++) {
        this.showVariant(this.forms[i]);
      }
    }

    async showVariant(variant, opts = {}) {
      const Service = getServiceForFormType(variant.form_type);
      const payload = await this.performVariantPreload(variant);
      const events = payload?.events;

      // Stop execution if variant is Social Proof but has no events.
      if (variant.goal === FormGoals.SOCIAL_PROOF && Array.isArray(events) && events.length === 0) {
        return;
      }

      const extendedVariant = _.extend(variant, payload);
      const newVariant = new Service(this.api, this, extendedVariant, opts);

      this.services.push(newVariant);
    }

    onCloseWelcomeMat() {
      this.showVariants();
    }

    getShownCookie(shownCookie, popup) {
      const cookie = this.getCookie(shownCookie);
      if (!cookie || popup.cookie_lifetime_interval === 'always') return false;

      // Old cookies
      if (cookie === 'true') return true;

      // See: https://github.com/moment/moment/issues/1407
      // moment() constructor should only be sent an ISO string which our cookie date is not
      // "Thu Nov 24 2016 14:50:51 GMT+1030 (ACDT)"
      const cookieDate = moment(new Date(cookie));
      switch (popup.cookie_lifetime_interval) {
        case 'minute':
          cookieDate.add(popup.cookie_lifetime, 'minutes');
          break;

        case 'hour':
          cookieDate.add(popup.cookie_lifetime, 'hours');
          break;

        case 'day':
          cookieDate.add(popup.cookie_lifetime, 'days');
          break;

        case 'month':
          cookieDate.add(popup.cookie_lifetime, 'months');
          break;

        case 'year':
          cookieDate.add(popup.cookie_lifetime, 'years');
          break;

        default:
          break;
      }

      return cookieDate.isAfter(moment());
    }

    setShownCookie(shownCookie, popup) {
      if (popup.cookie_lifetime !== -1) {
        const d = new Date();
        d.setFullYear(d.getFullYear() + 30);
        this.setCookie(shownCookie, new Date(), { expires: d, path: '/' });
      }
    }

    getSubscribedCookie() {
      return (this.getJSONCookie(SUBSCRIBED_COOKIE) === true);
    }

    setSubscribedCookie() {
      const d = new Date();
      d.setFullYear(d.getFullYear() + 10);
      this.setCookie(SUBSCRIBED_COOKIE, true, { expires: d, path: '/' });
    }

    getCtaCookie() {
      return (this.getJSONCookie(CTA_COOKIE) === true);
    }

    setCtaCookie() {
      const d = new Date();
      d.setFullYear(d.getFullYear() + 10);
      this.setCookie(CTA_COOKIE, true, { expires: d, path: '/' });
    }

    getPushSubscribedCookie() {
      return (this.getJSONCookie(PUSH_SUBSCRIBED_COOKIE) === true);
    }

    setPushSubscribedCookie() {
      const d = new Date();
      d.setFullYear(d.getFullYear() + 10);
      this.setCookie(PUSH_SUBSCRIBED_COOKIE, true, { expires: d, path: '/' });
    }

    getOptOutCookie(optOutCookie) {
      return (this.getJSONCookie(optOutCookie) === true);
    }

    setOptOutCookie(optOutCookie) {
      const d = new Date();
      d.setFullYear(d.getFullYear() + 10);
      this.setCookie(optOutCookie, true, { expires: d, path: '/' });
    }

    isNotPushCompatible() {
      const pushCompatible = ['chrome', 'firefox', 'opera'];
      let browserType;

      if (/Opera|OPR\//.test(navigator.userAgent) && 'serviceWorker' in navigator) {
        browserType = 'opera'
      } else if ('chrome' in window && 'serviceWorker' in navigator) {
        browserType = 'chrome'
      } else if (/Firefox[\/\s](\d+\.\d+)/.test(navigator.userAgent) && 'serviceWorker' in navigator) {
        browserType = 'firefox'
      } else if ('safari' in window && 'object' === typeof window.safari && 'pushNotification' in window.safari) {
        browserType = 'safari'
      } else {
        browserType = 'not-supported';
      }

      return (pushCompatible.indexOf(browserType) === -1);
    }

    async performVariantPreload(variant) {
      if (variant.goal === FormGoals.SOCIAL_PROOF) {
        const events = await this.fetchProofEvents(variant.id);

        return { events };
      }

      return {};
    }

    async fetchProofEvents(eventSourceId) {
      try {
        const { api: { properties: { sumoUrl, siteId } } } = this;
        const url = `${sumoUrl}/api/v2/sites/${siteId}/event-sources/${eventSourceId}/events`;
        const { data: response } = await axios.get(url, {
          params: {
            eventSourceId,
          },
        });

        return response.events;
      } catch (err) {
        console.error('[Sumo.Core] Error loading event data.');
      }

      return [];
    }
  }

  return AppService;
});
